import React from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceCard from '../components/ServiceCard';
import { trackBookCtaClick } from '../utils/gtag';
import './Home.css';

function Home({ services }) {
  const navigate = useNavigate();

  const goToBookFrom = (label) => {
    trackBookCtaClick(label);
    navigate('/book');
  };

  const goToBook = () => goToBookFrom('hero');
  const goToBookForJumpStart = () => goToBookFrom('jump_start');

  return (
    <main className="main-content">
      {/* Hero Section - Clear Value Proposition */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <div className="hero-badge">
              <svg className="hero-badge-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10.1V6c0-.6-.4-1-1-1H3c-.6 0-1 .4-1 1v10c0 .6.4 1 1 1h1"/>
                <circle cx="7" cy="17" r="2"/>
                <circle cx="15" cy="17" r="2"/>
              </svg>
              Mobile Service in Sacramento
            </div>
            <h1>Oil Change at Your Home or Office in Sacramento</h1>
            <p className="hero-subtitle">
              Skip the shop. We come to you and service your car right where it is parked in Sacramento County.
              Same-day appointments available • Limited spots daily
            </p>
            <div className="hero-cta">
              <button className="hero-btn-primary" onClick={goToBook}>
                BOOK NOW — $125
              </button>
              <a href="tel:9168188494" className="hero-btn-secondary">
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Call Us
              </a>
              <a href="sms:9168188494" className="hero-btn-text">
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Text Us
              </a>
            </div>
            <p className="hero-pickup-note">Pickup truck prices may vary</p>
            <button
              className="hero-btn-emergency"
              onClick={goToBookForJumpStart}
            >
              NEED A JUMP START? — $99
            </button>
            <div className="hero-trust-badges">
              <div className="trust-badge">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
                Licensed & Insured
              </div>
              <div className="trust-badge">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <path d="M9 16l2 2 4-4"/>
                </svg>
                7-Day Guarantee
              </div>
              <div className="trust-badge">
                <svg className="trust-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
                Full Synthetic Oil
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/main_pic_oil.webp" alt="NorCal Mobile Oil Change technicians" />
            <div className="hero-image-badge">
              <span className="badge-price">$125</span>
              <span className="badge-text">Mobile Oil Change</span>
              <span className="badge-note">Pickup truck prices may vary</span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-section">
        <div className="section-container">
          <h2>How It Works</h2>
          <p className="section-subtitle">No shop. No waiting. No hassle.</p>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                  <line x1="16" y1="2" x2="16" y2="6"/>
                  <line x1="8" y1="2" x2="8" y2="6"/>
                  <line x1="3" y1="10" x2="21" y2="10"/>
                  <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01M16 18h.01"/>
                </svg>
              </div>
              <h3>Book Online</h3>
              <p>Pick your service, choose a date and time, enter your location</p>
            </div>
            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9L18 10.1V6c0-.6-.4-1-1-1H3c-.6 0-1 .4-1 1v10c0 .6.4 1 1 1h1"/>
                  <circle cx="7" cy="17" r="2"/>
                  <circle cx="15" cy="17" r="2"/>
                  <path d="M9 17h4"/>
                  <path d="M13 6v4h5"/>
                </svg>
              </div>
              <h3>We Come to You</h3>
              <p>Our mobile unit arrives at your home, office, or anywhere you need</p>
            </div>
            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </div>
              <h3>Relax & Wait</h3>
              <p>Oil change done in less than 1 hour while you continue your day</p>
            </div>
            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3>Done!</h3>
              <p>Pay securely and get back to what matters. It's that simple.</p>
            </div>
          </div>

          <div className="inline-cta-banner">
            <div className="inline-cta-text">
              <h3>Save Your Time</h3>
              <p>Skip the shop. Book your mobile oil change in under a minute.</p>
            </div>
            <button
              className="inline-cta-btn"
              onClick={() => goToBookFrom('after_how_it_works')}
            >
              BOOK NOW — $125
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services-section" id="services">
        <div className="section-container">
          <h2>Our Services</h2>
          <p className="section-subtitle">
            Professional oil change with premium full synthetic oil
          </p>
          <p className="contact-info">
            Questions or same-day service?{' '}
            <a href="tel:9168188494" className="contact-action-link">Call Us</a>
            {' '}or{' '}
            <a href="sms:9168188494" className="contact-action-link">Text Us</a>
          </p>

          <div className="services-grid">
            {services.map(service => (
              <ServiceCard
                key={service.id}
                service={service}
                onBookNow={() => goToBookFrom(`service_card_${service.category}`)}
              />
            ))}
          </div>

          <div className="services-notes">
            <div className="note-item">
              <svg className="note-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <p><strong>Euro vehicles:</strong> Additional charge due to specialty parts and filters</p>
            </div>
            <div className="note-item">
              <svg className="note-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              <p><strong>Extra oil:</strong> $10 per additional quart beyond included amount</p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section - Testimonials */}
      <section className="testimonials-section">
        <div className="section-container">
          <h2>Trusted by local drivers</h2>
          <p className="section-subtitle">We’ve helped drivers across Sacramento save time and avoid the hassle of going to a shop.</p>

          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "I called Dennis and team yesterday and they confirmed my appointment the next following day. I called for an oil change and upon arrival he did the job in under an hour. The price is as advertised no hidden fees. He even showed me pictures of his work as proof. This is a new start up company and to show appreciation for my business they gave me a gift for using their service. I 100% recommend them!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">A</div>
                <div className="author-info">
                  <strong>Anya C.</strong>
                  <span>Yelp Review</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "Steve was excellent. He arrived on time, was professional, and completed the oil change quickly. He also walked me through my filters and explained what he was checking, which I really appreciated. Great communication and a very smooth experience. Highly recommend. Also they come to you so makes it a lot easier too!"
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">N</div>
                <div className="author-info">
                  <strong>Nick V.</strong>
                  <span>Yelp Review</span>
                </div>
              </div>
            </div>

            <div className="testimonial-card">
              <div className="testimonial-stars">★★★★★</div>
              <p className="testimonial-text">
                "Same day service. Excellent job. The team is thorough and works efficiently. Worth the money, and will be using them only in the future."
              </p>
              <div className="testimonial-author">
                <div className="author-avatar">J</div>
                <div className="author-info">
                  <strong>Jordyn B.</strong>
                  <span>Yelp Review</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mid-page CTA banner — long-scroll re-engagement */}
      <section className="mid-cta-section">
        <div className="section-container">
          <div className="mid-cta-content">
            <h2>Ready When You Are</h2>
            <p>Same-day service available. Licensed, insured, and at your door in 1 hour.</p>
            <button
              className="mid-cta-btn"
              onClick={() => goToBookFrom('mid_page')}
            >
              BOOK NOW
            </button>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="why-choose-section">
        <div className="section-container">
          <h2>Why Do People Choose NorCal Mobile Oil Change?</h2>
          <p className="section-subtitle">More than just an oil change — it's convenience redefined</p>

          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <h3>We Come to Your Home or Office</h3>
              <p>Home, office, gym parking lot — anywhere that's convenient for you</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"/>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                </svg>
              </div>
              <h3>Transparent Pricing</h3>
              <p>No hidden fees. Price includes labor, oil, filter, and cleanup</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3>Save Your Time</h3>
              <p>No waiting in line, no driving. Continue your day while we work</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <polyline points="9 12 11 14 15 10"/>
                </svg>
              </div>
              <h3>7-Day Guarantee</h3>
              <p>Full workmanship guarantee on every service we provide</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.555C21.965 6.012 17.461 2 12 2z"/>
                  <circle cx="7.5" cy="11.5" r="1.5" fill="currentColor"/>
                  <circle cx="12" cy="7.5" r="1.5" fill="currentColor"/>
                  <circle cx="16.5" cy="11.5" r="1.5" fill="currentColor"/>
                </svg>
              </div>
              <h3>Premium Products</h3>
              <p>Only full synthetic oil and quality OEM-spec filters</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 3l1.5 4.5H18l-3.5 2.5 1 4.5L12 12l-3.5 2.5 1-4.5L6 7.5h4.5L12 3z"/>
                  <path d="M5 21l2-2m12 2l-2-2M12 17v4"/>
                </svg>
              </div>
              <h3>Clean Service</h3>
              <p>We use drip pans and clean up completely — no mess left behind</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="service-area-section">
        <div className="section-container">
          <h2>Service Area</h2>
          <p className="section-subtitle">Proudly serving Sacramento County and surrounding areas</p>

          <div className="service-area-content">
            <div className="area-map-placeholder">
              <div className="map-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
              </div>
              <p>Sacramento Metro Area</p>
            </div>
            <div className="area-cities">
              <h3>Cities We Serve:</h3>
              <div className="cities-grid">
                <span className="city-tag">Sacramento</span>
                <span className="city-tag">Elk Grove</span>
                <span className="city-tag">Roseville</span>
                <span className="city-tag">Folsom</span>
                <span className="city-tag">Rancho Cordova</span>
                <span className="city-tag">Citrus Heights</span>
                <span className="city-tag">Carmichael</span>
                <span className="city-tag">Fair Oaks</span>
                <span className="city-tag">Orangevale</span>
                <span className="city-tag">West Sacramento</span>
                <span className="city-tag">Davis</span>
                <span className="city-tag">Woodland</span>
              </div>
              <p className="area-note">
                <strong>Not sure if we serve your area?</strong>{' '}
                <a href="tel:9168188494">Call Us</a> or{' '}
                <a href="sms:9168188494">Text Us</a> — we cover up to 25 miles from Sacramento!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-section">
        <div className="section-container">
          <h2>About NorCal Mobile Oil Change</h2>
          <div className="about-image">
            <img src="/images/norcal-phone.jpg" alt="NorCal Mobile Oil Change booking on phone" />
          </div>
          <div className="about-content">
            <p>
              NorCal Mobile Oil Change brings premium vehicle maintenance directly to your driveway, office, or wherever life takes you.
              No more waiting rooms, no more wasted time — just convenient, professional service that comes to you.
            </p>
            <p>
              Founded by <strong>Denis Divakov</strong> and <strong>Iurii Vadaturschii</strong>, NorCal Mobile Oil Change was born from a simple mission:
              to make car care easier, faster, and more transparent. We believe your time is valuable,
              and maintaining your vehicle shouldn't interrupt your day.
            </p>
            <p>
              With modern equipment, quality parts, and experienced technicians, we deliver dealership-level service
              at your convenience. Whether it's a routine oil change or specialized maintenance,
              NorCal Mobile Oil Change keeps you moving forward — we come to you!
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Ready to Save Time?</h2>
            <p>Book your oil change at home or anywhere today. No shop visits, no waiting.</p>
            <div className="cta-buttons">
              <button className="cta-btn-primary" onClick={() => goToBookFrom('bottom_cta')}>
                BOOK NOW — $125
              </button>
              <a href="tel:9168188494" className="cta-btn-secondary">
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Call Us
              </a>
              <a href="sms:9168188494" className="cta-btn-text">
                <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Text Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Home;
